import * as React from 'react'

import useI18n from '@store/i18n/useI18n'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import Button from '@components/button/button'
import Loader from 'react-loader-spinner'
import { BsCheck } from 'react-icons/bs'
import { VscError } from 'react-icons/vsc'
import UseAnimations from 'react-useanimations'
import DownloadAnimation from 'react-useanimations/lib/download'
import apis from '@app/api/api'
import { downloadBase64Image, getImageTypeFromBase64 } from '@utils/image'

interface Props {
  pictures: PicturesToDownload[]
  cancel: () => void
}

type PictureState = 'waiting' | 'download' | 'success' | 'error'

interface PicturesState {
  id: string
  name: string
  path: string
  state: PictureState
}

const DownloadingComponent = ({ pictures, cancel }: Props) => {
  const i18n = useI18n()
  const [picturesStates, setPicturesStates] = React.useState<PicturesState[]>([])

  React.useEffect(() => {
    setPicturesStates(pictures.map((p, idx) => ({ ...p, state: idx === 0 ? 'download' : 'waiting' })))
  }, [])

  React.useEffect(() => {
    const picIdx = picturesStates.findIndex((p) => p.state === 'download')

    if (picIdx > -1) {
      const picture = picturesStates[picIdx]
      apis.declaration.pictures
        .getPicture(picture.id)
        .then((base64Image) => {
          downloadBase64Image(base64Image, picture.name)

          setTimeout(
            () =>
              picIdx !== picturesStates.length - 1
                ? setPicturesStates([
                    ...picturesStates.slice(0, picIdx),
                    { ...picture, state: 'success', path: base64Image },
                    { ...picturesStates[picIdx + 1], state: 'download' },
                    ...picturesStates.slice(picIdx + 2, picturesStates.length),
                  ])
                : setPicturesStates([
                    ...picturesStates.slice(0, picIdx),
                    { ...picture, state: 'success', path: base64Image },
                  ]),
            500
          )
        })
        .catch(() => {
          setTimeout(
            () =>
              picIdx !== picturesStates.length - 1
                ? setPicturesStates([
                    ...picturesStates.slice(0, picIdx),
                    { ...picturesStates[picIdx], state: 'error' },
                    { ...picturesStates[picIdx + 1], state: 'download' },
                    ...picturesStates.slice(picIdx + 2, picturesStates.length),
                  ])
                : setPicturesStates([
                    ...picturesStates.slice(0, picIdx),
                    { ...picturesStates[picIdx], state: 'error' },
                  ]),
            500
          )
        })
    } else {
      return
    }
  }, [picturesStates])

  const renderState = (state: PictureState) => {
    switch (state) {
      case 'waiting':
        return <Loader type="TailSpin" color={Theme.colors.classicButtonLoader} height={35} />
      case 'download':
        return (
          <UseAnimations
            animation={DownloadAnimation}
            size={35}
            autoplay
            strokeColor={Theme.colors.classicButtonLoader}
          />
        )
      case 'success':
        return <BsCheck size={30} color={Theme.colors.classicButtonLoader} />
      case 'error':
        return <VscError size={30} color={Theme.colors.classicButtonLoader} />
      default:
        return
    }
  }

  return (
    <Background>
      <SelectorContent onClick={() => null}>
        <ScreenTitleContainer>
          <ScreenTitle>{i18n.t('components.downloadComponent.title')}</ScreenTitle>
          <Back onClick={cancel}>{i18n.t('components.downloadComponent.back')}</Back>
        </ScreenTitleContainer>
        <OptionList>
          {picturesStates.map((p, idx) => (
            <PictureLine key={idx}>
              {p.path && <Picture src={p.path} />}
              <PictureLibelle>{`${p.name}_${idx + 1}.${getImageTypeFromBase64(p.path)}`}</PictureLibelle>
              <PictureState>{renderState(p.state)}</PictureState>
            </PictureLine>
          ))}
        </OptionList>
        <ButtonContainer>
          <Button
            title={i18n.t('components.downloadComponent.closeAction')}
            onClick={cancel}
            status="active"
            type="navigation"
            size="small"
          />
        </ButtonContainer>
      </SelectorContent>
    </Background>
  )
}

export default DownloadingComponent

const Background = styled('div')`
  background-color: ${(props) => props.theme.colors.backgroundColor};
  position: absolute;
  cursor: pointer;
  z-index: 15;
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
  font-weight: bold;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  padding: 15px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const SelectorContent = styled('div')`
  flex: 1;
  z-index: 2;
  display: flex;
  padding: 10px;
  height: ${window.innerHeight * 0.6}px;
  max-width: ${window.innerWidth * 0.5}px;
  border-radius: 20px;
  flex-direction: column;
  background-color: white;
  box-shadow: 0px 0px 15px ${(props) => props.theme.colors.menuShadow};
`
const OptionList = styled('div')`
  flex: 1;
  display: flex;
  overflow: scroll;
  flex-direction: column;
  padding: 0 25px;
`
const PictureLine = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px;
  min-height: 100px;

  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 2px;
`
const Picture = styled('img')`
  width: 75px;
  height: 75px;
  object-fit: cover;
  border-radius: 10px;
`
const PictureLibelle = styled('div')`
  flex: 1;
  display: flex;
  padding: 10px 20px;
  ${(props) => props.theme.fonts.backLibelle};
`
const PictureState = styled('div')`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`
const ButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0 10px 0;
`
